/**
 * The CenoX AI workspace.
 *
 * Chat, which is built, lives at `/chat` (see ChatView): threads live on the
 * server, answers stream, and a conversation is private to the person who
 * started it. Usage is built too. Everything else in the left menu has been
 * designed and not implemented, and says so rather than showing an empty
 * version of itself.
 *
 * Chat calls the same `/ai` endpoints the Business assistant does — one AI
 * engine, several surfaces — rather than a second one built here. When the
 * provider is not configured the server says so, and that answer is shown as
 * it arrives.
 */

import type { ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FolderOpen, Loader2 } from 'lucide-react';
import { Card, EmptyState } from '../../components/ui';
import { UsageView } from './UsageView';

/** The sections that are designed and not yet built. */
const SECTIONS: Record<string, { title: string; description: string }> = {
  agents: {
    title: 'Agents',
    description:
      'Saved configurations that run on your behalf. An agent will hold no permissions of its own — it acts as whoever invokes it, and a scheduled run names the person it acts for.',
  },
  projects: {
    title: 'Projects',
    description: 'Somewhere to group conversations, files and knowledge around one piece of work.',
  },
  knowledge: {
    title: 'Knowledge',
    description:
      'Your own documents, searchable. Retrieval will resolve what you are allowed to read first, then search inside it.',
  },
  files: { title: 'Files', description: 'Files you bring to CenoX AI, and what it made from them.' },
  developer: { title: 'Developer Tools', description: 'Prompts, schemas and the API surface.' },
  images: { title: 'Images', description: 'Generate and edit images.' },
  audio: { title: 'Audio', description: 'Speech, transcription and sound.' },
  video: { title: 'Video', description: 'Generate and edit video.' },
  evaluations: { title: 'Evaluations', description: 'Compare models and measure answers.' },
  'api-keys': { title: 'API Keys', description: 'Keys for reaching CenoX AI from your own code.' },
  settings: { title: 'Settings', description: 'How CenoX AI behaves in this workspace.' },
};

/** Projects are real; they are made and managed from Chat's History. Say where. */
function ProjectsInChat(): ReactNode {
  return (
    <div className="mx-auto w-full max-w-3xl">
      <h1 className="text-xl font-semibold tracking-tight text-slate-900">Projects</h1>
      <p className="mt-1 text-sm text-slate-500">
        Folders for conversations, with standing instructions for every answer inside them.
      </p>
      <Card className="mt-4">
        <EmptyState
          icon={<FolderOpen className="size-8" />}
          title="Projects live in Chat"
          description="Create, rename and archive projects, and file conversations into them, from History in Chat."
          action={
            <Link
              to="/chat?history=open"
              className="bg-cx-gradient inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm hover:brightness-110"
            >
              Open projects in Chat
            </Link>
          }
        />
      </Card>
    </div>
  );
}

export function AiWorkspace(): ReactNode {
  const { section } = useParams<{ section: string }>();

  // Chat moved to its own address; AiEntry forwards `/ai` before this renders.
  if (!section) return <Navigate to="/chat" replace />;
  // Built. Everything still in SECTIONS is not.
  if (section === 'usage') return <UsageView />;
  // Built too, inside Chat (#58): folders live above the conversation list.
  if (section === 'projects') return <ProjectsInChat />;

  const known = SECTIONS[section];
  if (!known) {
    return (
      <Card>
        <EmptyState title="That is not a CenoX AI section" description="Use the menu on the left." />
      </Card>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <h1 className="text-xl font-semibold tracking-tight text-slate-900">{known.title}</h1>
      <p className="mt-1 text-sm text-slate-500">{known.description}</p>

      <Card className="mt-4">
        <EmptyState
          icon={<Loader2 className="size-8" />}
          title="Not built yet"
          description="This section is designed and not yet implemented. Nothing here is a placeholder for data you already have — there is none."
        />
      </Card>
    </div>
  );
}
